import React from 'react';
import { CheckCircle2Icon, XCircleIcon, RotateCcwIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { PASS_MARK } from '../../data/academyCourses';
import { useAcademy } from '../../context/Academy';
import { track } from '../../lib/analytics';
import { quizKey } from '../../utils/academyProgress';
import type { Course, QuizResult } from '../../types/academy';
interface QuizBlockProps {
  course: Course;
  moduleIndex: number;
}
export function QuizBlock({
  course,
  moduleIndex
}: QuizBlockProps) {
  const {
    state,
    submitQuiz
  } = useAcademy();
  const mod = course.modules[moduleIndex];
  const quiz = mod.quiz ?? [];
  const stored = state.quizzes[quizKey(course.slug, moduleIndex)];
  const [answers, setAnswers] = React.useState<Record<number, string>>({});
  const [result, setResult] = React.useState<QuizResult | null>(null);
  const [missing, setMissing] = React.useState(false);
  React.useEffect(() => {
    setAnswers({});
    setResult(null);
    setMissing(false);
  }, [course.slug, moduleIndex]);
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (Object.keys(answers).length < quiz.length) {
      setMissing(true);
      return;
    }
    setMissing(false);
    const outcome = submitQuiz(course, moduleIndex, answers);
    track('academy_quiz_submitted', {
      source: course.slug,
      score: outcome.score
    });
    setResult(outcome);
  };
  const retry = () => {
    setAnswers({});
    setResult(null);
  };
  if (stored?.passed && !result) {
    return <div className="flex items-start gap-3 rounded-md border border-line bg-panel p-5" data-id="element-506" data-mp="2jbodb8l5hov8">
        <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#4ADE80]" aria-hidden="true" data-id="element-507" data-mp="2xb79d30ot9r6" />
        <div data-id="element-508" data-mp="2sb30nz60tjbi">
          <p className="font-medium text-ink" data-id="element-509" data-mp="2sajhkf4mwovw">Module quiz passed with {stored.score}%</p>
          <p className="mt-1 text-[14px] text-body" data-id="element-510" data-mp="cjnki6q93g96">{mod.quizFocus}</p>
          <button type="button" onClick={retry} className="mt-3 text-small text-royal underline underline-offset-4 hover:text-ink" data-id="element-511" data-mp="7ldm7n98bzjc">
            Take it again
          </button>
        </div>
      </div>;
  }
  return <form onSubmit={onSubmit} className="rounded-md border border-line bg-panel p-5 sm:p-7" aria-labelledby="quiz-heading" data-id="element-512" data-mp="36bwxc4ttxpsr">
      <h2 id="quiz-heading" className="font-display text-[22px] text-ink" data-id="element-513" data-mp="3afbh96kw2qbh">
        Module quiz
      </h2>
      <p className="mt-1 text-[14px] text-body" data-id="element-514" data-mp="3fbtezmhsxoeb">
        {mod.quizFocus}. Pass mark {PASS_MARK}%. You can retry as often as you like.
      </p>

      <ol className="mt-7 space-y-7" data-id="element-515" data-mp="2mofhnna6yqto">
        {quiz.map((q, qi) => {
        const chosen = answers[qi];
        const isCorrect = result && chosen === q.correctId;
        return <li key={q.question} data-id="element-516" data-mp="2rlpdbko6bypy" data-mpi={qi}>
              <fieldset disabled={Boolean(result)} data-id="element-517" data-mp="2weiiuargvb33">
                <legend className="text-[16px] font-medium leading-snug text-ink" data-id="element-518" data-mp="2wgbs7xtgs9es">
                  {qi + 1}. {q.question}
                </legend>
                <div className="mt-3 space-y-2" data-id="element-519" data-mp="23x65t7cy7pyx">
                  {q.options.map(o => {
                const selected = chosen === o.id;
                const reveal = result && o.id === q.correctId;
                return <label key={o.id} className={`flex cursor-pointer items-start gap-3 rounded-sm border px-4 py-3 text-[15px] transition-colors duration-150 ${reveal ? 'border-[#4ADE80]/60 bg-[#4ADE80]/8 text-ink' : selected ? 'border-gold bg-charcoal text-ink' : 'border-line text-body hover:border-gold/50'}`} data-id="element-520" data-mp="1rd96kvry9gh8">
                        <input type="radio" name={`q-${qi}`} value={o.id} checked={selected} onChange={() => setAnswers(a => ({
                    ...a,
                    [qi]: o.id
                  }))} className="mt-1 accent-[#FF5C1A]" data-id="element-521" data-mp="yrpsm6fyq2yk" />
                        {o.text}
                      </label>;
              })}
                </div>
                {result && <p className={`mt-3 flex items-start gap-2 text-[14px] ${isCorrect ? 'text-[#4ADE80]' : 'text-[#FF8B7A]'}`} data-id="element-522" data-mp="2g1wuj74bnxiv">
                    {isCorrect ? <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-523" data-mp="263qrgm6s4lcf" /> : <XCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-524" data-mp="2b44svldvkx1h" />}
                    <span data-id="element-525" data-mp="1x5548dqd4i5b">
                      <span className="font-medium" data-id="element-526" data-mp="1x5x0lqrsajwn">{isCorrect ? 'Correct. ' : 'Not quite. '}</span>
                      <span className="text-body" data-id="element-527" data-mp="224hz9lre0viz">{q.explanation}</span>
                    </span>
                  </p>}
              </fieldset>
            </li>;
      })}
      </ol>

      <div aria-live="polite" data-id="element-528" data-mp="af8m0v6jsy58">
        {missing && <p className="mt-6 text-small text-[#FF8B7A]" data-id="element-529" data-mp="ag0hm605avvi">Please answer every question before submitting.</p>}
        {result && <div className={`mt-8 rounded-sm px-5 py-4 ${result.passed ? 'bg-[#4ADE80]/10' : 'bg-charcoal'}`} data-id="element-530" data-mp="5e1cororbqwg">
            <p className="font-display text-[20px] text-ink" data-id="element-531" data-mp="j9dj3wc9z5t7">
              {result.score}%. {result.passed ? 'Module complete.' : `You need ${PASS_MARK}% to pass.`}
            </p>
            <p className="mt-1 text-[14px] text-body" data-id="element-532" data-mp="t3vpxacznhh8">
              {result.passed ? 'Your progress has been saved.' : 'Review the explanations above, then try again.'}
            </p>
          </div>}
      </div>

      <div className="mt-6" data-id="element-533" data-mp="1242a1f36zfpd">
        {result ? !result.passed && <Button type="button" variant="secondary" onClick={retry} data-id="element-534" data-mp="y2p2n0kgb4gc">
              <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-535" data-mp="33yjf8qfi7z6t" />
              Retry quiz
            </Button> : <Button type="submit" data-id="element-536" data-mp="2zyj176wbhq0a">Submit answers</Button>}
      </div>
    </form>;
}